import FormLabel from '@c/forms/controls/FormLabel';
import BaseModal from '@c/modals/BaseModal';
import { useQuery } from '@tanstack/react-query';
import Button from '@ui/Button';
import { Label } from '@ui/Label';
import Select from '@ui/Select';
import {
  getMainCatsClient,
  getSubcatsClient,
} from '@util/firestore/categories';
import { useCreateListing } from '../_context';
import { CategoryDocument } from 'models/category';

export default function EditCategoryModal({
  isOpen,
  dismiss,
}: {
  isOpen: boolean;
  dismiss: () => void;
}) {
  const { categoryInfo, setCategoryInfo, form } = useCreateListing();
  const { data: sports } = useQuery({
    queryKey: ['sports'],
    queryFn: () => getMainCatsClient(),
  });
  const { data: categories } = useQuery({
    queryKey: ['categories', categoryInfo[0]?.id],
    queryFn: () => getSubcatsClient(1, categoryInfo[0]?.id),
    enabled: !!categoryInfo[0]?.id,
  });
  const { data: cat2s } = useQuery({
    queryKey: ['category2s', categoryInfo[1]?.id],
    queryFn: () => getSubcatsClient(2, categoryInfo[1]?.id),
    enabled: !!categoryInfo[1]?.id,
  });
  const { data: cat3s } = useQuery({
    queryKey: ['category3s', categoryInfo[2]?.id],
    queryFn: () => getSubcatsClient(3, categoryInfo[2]?.id),
    enabled: !!categoryInfo[2]?.id,
  });
  const { data: cat4s } = useQuery({
    queryKey: ['category4s', categoryInfo[3]?.id],
    queryFn: () => getSubcatsClient(4, categoryInfo[3]?.id),
    enabled: !!categoryInfo[3]?.id,
  });
  const renderSubcategorySelect = (
    options: CategoryDocument[],
    index: number
  ) => {
    return (
      <>
        <Select
          options={
            options
              .filter((o) => !o.is_seo_only)
              .map((option) => ({
                id: option.id,
                label: option.name,
                value: option.id,
              })) || []
          }
          key={options[0]?.id}
          allowEmpty={false}
          placeholder="Select a subcategory"
          onChange={(option) => {
            const currCategoryInfo = [...categoryInfo];
            currCategoryInfo[index] = options.find(
              (o) => o.id === option.value
            )!;
            setCategoryInfo(currCategoryInfo);
          }}
          value={categoryInfo[index]?.id ?? ''}
        />
      </>
    );
  };
  return (
    <BaseModal
      isOpen={isOpen}
      title={'Edit Sports & Categories'}
      dismiss={dismiss}
      overflowType="visible"
    >
      <div className="flex w-full min-w-[50rem] flex-col gap-[1.6rem] p-8">
        <FormLabel value="Sport" required>
          <Select
            allowEmpty={false}
            options={
              sports?.map((s) => ({
                value: s.id,
                id: s.id,
                label: s.name,
              })) ?? []
            }
            value={categoryInfo[0]?.id}
            onChange={(e) => {
              const sportDoc = sports?.find((s) => s.id === e.id);
              if (sportDoc) setCategoryInfo([sportDoc]);
            }}
          ></Select>
        </FormLabel>
        <div className="my-8 h-px w-full bg-brand-light-gray" />
        <FormLabel value="Categories" required={true}>
          <Select
            options={
              categories
                ?.filter((o) => !o.is_seo_only)
                .map((section) => {
                  return {
                    id: section.id,
                    label: section.name,
                    value: section.id,
                  };
                }) ?? []
            }
            value={categoryInfo[1]?.id ?? ''}
            placeholder="Select a category"
            onChange={(option) => {
              const currCategoryInfo = [categoryInfo[0]];
              currCategoryInfo.push(
                categories?.find((o) => o.id === option.value)!
              );
              setCategoryInfo([...currCategoryInfo]);
            }}
            allowEmpty={false}
          />
        </FormLabel>
        {!!cat2s?.length && (
          <>
            <div className="my-8 h-px w-full bg-brand-light-gray" />
            <FormLabel value="Subcategory" required>
              <div className="flex flex-col gap-y-6">
                {!!cat2s.length && renderSubcategorySelect(cat2s, 2)}

                {!!cat3s?.length && renderSubcategorySelect(cat3s, 3)}

                {!!cat4s?.length && renderSubcategorySelect(cat4s, 4)}
              </div>
            </FormLabel>
          </>
        )}
        {form.formState.errors.category && (
          <Label className="text-[1.3rem] text-red-500">
            {form.formState.errors.category.message}
          </Label>
        )}
        <div className="mt-[1.6rem] flex gap-[1.6rem]">
          <Button
            width="fluid"
            text="Cancel"
            type="tertiary"
            onClick={() => {
              setCategoryInfo([]);
              dismiss();
            }}
          />
          <Button
            width={'fluid'}
            text="Save Changes"
            type="secondary"
            onClick={() => {
              const canContinue =
                categoryInfo[1]?.name === 'Bikes' ||
                (categoryInfo[0]?.name === 'Casual' &&
                  categoryInfo.length > 1) ||
                categoryInfo[0]?.name === 'Collectibles' ||
                categoryInfo.length > 2;
              if (!canContinue) {
                form.setError('category', {
                  type: 'required',
                  message: 'Please select all categories',
                });
                return;
              }
              const lastCat = categoryInfo[categoryInfo.length - 1];
              if (!lastCat) return;
              form.setValue('ride_types', lastCat.ride_types!);
              form.setValue('category', lastCat.product_cats.category!);
              if (lastCat.product_cats.category !== 'Dirt Bikes') {
                form.setValue('category1', lastCat.product_cats.category1!);
                form.setValue('category2', lastCat.product_cats.category2!);
                form.setValue('category3', lastCat.product_cats.category3!);
              }

              form.setValue(
                'categories',
                [
                  lastCat.product_cats.category!,
                  lastCat.product_cats.category1!,
                  lastCat.product_cats.category2!,
                  lastCat.product_cats.category3!,
                ].filter(Boolean)
              );
              dismiss();
            }}
          />
        </div>
      </div>
    </BaseModal>
  );
}
